import React, { useState, useEffect, useRef } from "react";
import { Folder, Video, Check, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';

interface PanoptoItem {
  Id: string;
  Name: string;
  Type: "folder" | "file";
  Duration: any;
  children?: PanoptoItem[];
}

interface PanoptoTreeProps {  
  data: any;
  onUncheckedItems?: (uncheckedItemIds: string[]) => void;
  uncheckedItemIds?: string[];
  onRefresh?: () => void;
}

const PanoptoTree: React.FC<PanoptoTreeProps> = ({ data, onUncheckedItems, uncheckedItemIds = [] , onRefresh}) => {
  const [processedData, setProcessedData] = useState<PanoptoItem[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<PanoptoItem | null>(null);
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const [expandedFolders, setExpandedFolders] = useState<{ [key: string]: boolean }>({});
  const [sortField, setSortField] = useState<"Name" | "Duration">("Name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const checkedItemsRef = useRef<{ [folderId: string]: { [key: string]: boolean } }>({});
  const roundToTwo = (num: number) => Math.round(num * 100) / 100;

  useEffect(() => {
    if (data) {
      const processItems = (item: any): PanoptoItem => ({
        Id: item.Id,
        Name: item.Name,
        Type: item.Type,
        Duration: item?.Duration,
        children: [
          ...(item.Sessions || []).map((session: any) => ({
            Id: session.Id,
            Name: session.Name,
            Type: "file",
            Duration: session?.Duration,
          })),
          ...(item.Subfolders || []).map((subfolder: any) => processItems(subfolder)),
        ],
      });

      const processed = processItems(data);
      setProcessedData([processed]);
      setSelectedFolder(processed);

      if (checkedItemsRef.current[processed.Id]) {
        setCheckedItems(checkedItemsRef.current[processed.Id]);
      } else {
        const initializeCheckedItems = (items: PanoptoItem[], checked: boolean) => {
          const checkedState: { [key: string]: boolean } = {};
          const setCheckedState = (items: PanoptoItem[]) => {
            items.forEach(item => {
              checkedState[item.Id] = checked;
              if (item.children) {
                setCheckedState(item.children);
              }
            });
          };
          setCheckedState(items);
          return checkedState;
        };

        const newCheckedState = initializeCheckedItems([processed], true);
        checkedItemsRef.current[processed.Id] = newCheckedState;
        setCheckedItems(newCheckedState);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!uncheckedItemIds.length) return;
    setCheckedItems(prevChecked => {
      const newCheckedItems = { ...prevChecked };
      let hasChanges = false;
      uncheckedItemIds.forEach(id => {
        if (newCheckedItems[id] !== false) {
          newCheckedItems[id] = false;
          hasChanges = true;
        }
      });
      return hasChanges ? newCheckedItems : prevChecked;
    });
  }, [uncheckedItemIds]);

  const handleFolderClick = (folder: PanoptoItem) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folder.Id]: !prev[folder.Id],
    }));
  };

  const handleCheckboxChange = (itemId: string, isChecked: boolean) => {
    setCheckedItems(prev => {
      const updatedCheckedItems = { ...prev };
  
      // Function to recursively update children
      const updateChildren = (items: PanoptoItem[], checked: boolean) => {
        items.forEach(child => {
          updatedCheckedItems[child.Id] = checked; // Update child checkbox
          if (child.children) {
            updateChildren(child.children, checked);
          }
        });
      };
  
      updatedCheckedItems[itemId] = isChecked; // Update parent
      const item = findItemById(processedData, itemId); // Get item details
  
      if (item && item.children) {
        updateChildren(item.children, isChecked); // Recursively update children
      }
  
      // Notify parent component with unchecked items
      if (onUncheckedItems) {
        const uncheckedItemIds = Object.keys(updatedCheckedItems).filter(id => !updatedCheckedItems[id]);
        onUncheckedItems(uncheckedItemIds);
      }
  
      return updatedCheckedItems;
    });
  };
  
  // Helper function to find an item by ID in the processedData tree
  const findItemById = (items: PanoptoItem[], id: string): PanoptoItem | null => {
    for (const item of items) {
      if (item.Id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  

  const handleParentCheckboxChange = (item: PanoptoItem, isChecked: boolean) => {
    const updateChildren = (items: PanoptoItem[], checked: boolean) => {
      items.forEach(child => {
        handleCheckboxChange(child.Id, checked);
        if (child.children) {
          updateChildren(child.children, checked);
        }
      });
    };
    handleCheckboxChange(item.Id, isChecked);
    if (item.children) {
      updateChildren(item.children, isChecked);
    }
  };

  const formatDuration = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const pad = (num: number) => String(num).padStart(2, '0');
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
};


  const sortItems = (items: PanoptoItem[]): PanoptoItem[] => {
    return items
      .slice()
      .sort((a, b) => {
        if (sortField === "Name") {
          return sortDirection === "asc" ? a.Name.localeCompare(b.Name) : b.Name.localeCompare(a.Name);
        }
        if (sortField === "Duration") {
          return sortDirection === "asc"
          ? (a.Duration - b.Duration) || a.Name.localeCompare(b.Name)
          : (b.Duration - a.Duration) || b.Name.localeCompare(a.Name);
        }
        return 0;
      })
      .map(item => ({
        ...item,
        children: item.children ? sortItems(item.children) : [],
      }));
  };

  const handleSort = (field: "Name" | "Duration") => {
    setSortField(field);
    setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
  };

  const renderList = (items: PanoptoItem[], depth = 0) => {
    const sortedItems = sortItems(items);
  
    return (
      <>
        {/* Show headers only for the top-level folder */}
        {depth === 0 && (
          <div className="flex justify-between font-semibold p-2 border-b">
            <div className="flex items-center text-sm text-gray-500 cursor-pointer" onClick={() => handleSort("Name")}>
              <span>Name</span>
              {sortField === "Name" && (sortDirection === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
            </div>
            <div className="text-sm text-gray-500 cursor-pointer flex items-center space-x-1 min-w-[80px] " onClick={() => handleSort("Duration")}>
  <span>Duration</span>
  {sortField === "Duration" && (sortDirection === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
</div>
          </div>
        )}
  
        {sortedItems.map(item => (
          <div key={item.Id} className="w-full ">
            {/* Parent Item */}
            <div style={{ paddingLeft: depth * 15 }} className="flex justify-between p-2 hover:bg-gray-100 cursor-pointer">
              <div className="flex items-center p-2 rounded-md hover:bg-gray-100 cursor-pointer w-full" onClick={() => handleFolderClick(item)}>
                {item.Type === "folder" && (
                  <div className="mr-2">{expandedFolders[item.Id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
                )}
                <div
                  className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
                    checkedItems[item.Id] ? "bg-black" : "bg-white"
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    handleCheckboxChange(item.Id, !checkedItems[item.Id]);
                  }}
                >
                  {checkedItems[item.Id] && <Check className="h-4 w-4 text-white" />}
                </div>
                {item.Type === "folder" ? <Folder className="text-gray-500 ml-2 mr-2" /> : <Video className="text-gray-500 mr-2 ml-2" />}
                <span>{item.Name}</span>
              </div>
  
              {/* Show Duration only for top-level items */}
              {depth === 0 && <div className="text-sm text-gray-500 min-w-[80px] ">{item.Duration ? formatDuration(item.Duration) : "N/A"}</div>}
            </div>
  
            {/* Child Items - Make sure they appear **below** the parent */}
            {expandedFolders[item.Id] && item.children && item.children.length > 0 && (
              <div className="pl-6">{renderList(item.children, depth + 1)}</div>
            )}
          </div>
        ))}
      </>
    );
  };
  
  
  

  return (
    <div className="flex h-screen border ">
      <div className="w-3/4 p-4 overflow-y-auto min-h-[60%] max-h-[60%] pb-[10%]">{selectedFolder?.children?.length ? renderList(selectedFolder.children) : <div className="text-center text-gray-500">No items to display 
        <FontAwesomeIcon
  icon={faRefresh}
  className="h-4 w-4 pl-2 cursor-pointer text-gray-600 hover:text-black"
  onClick={onRefresh}
/>
</div>}</div>
    </div>
  );
};

export default PanoptoTree;
